import { getAccessToken } from 'utils/auth';
import { TMXPageData, getNewSessionId } from 'utils/threatmetrix';
import { getBackofficeUrl } from 'utils/urls';

/**
 * Fire-and-forget `/api/risk` for a DETAILS_CHANGE event (profile mutation).
 *
 * Mirrors the Unity callsites that call PostRisk without onSuccess/onFailure
 * callbacks after a successful profile update:
 *   - AvatarsView.cs:167                 (avatar change)
 *   - AddressModalView.cs:128            (address update)
 *   - AccountsModalView.cs:247           (account info change)
 *   - AddPhoneView.cs:154                (phone add)
 *   - ChangePasswordSlideView.cs:195     (password change)
 *
 * All five Unity sites pass `(0, DETAILS_CHANGE, TMXPageData.AccountUpdate)` —
 * the amount is 0 (no money in flight), the eventType is the canonical
 * details-change marker, and the page index is AccountUpdate. This helper
 * preserves that shape exactly.
 *
 * The request runs in the background — callers should NOT await it. Errors
 * are swallowed (parity with Unity: PostRisk is fire-and-forget, the result
 * has no UI consequence beyond what the backend records server-side).
 */
export function firePostRiskForDetailsChange(playerGUID: string): void {
  if (!playerGUID) return;

  void (async () => {
    try {
      const sessionId = await getNewSessionId(TMXPageData.AccountUpdate, playerGUID);
      if (!sessionId) return;

      const jwt = getAccessToken();
      await fetch(`${getBackofficeUrl()}/api/risk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          sessionId,
          eventType: 'details_change',
          playerGuid: playerGUID,
          amount: 0,
          pageId: TMXPageData.AccountUpdate
        })
      });
    } catch {
      // Fire-and-forget; swallow. Backend logs the absence server-side if needed.
    }
  })();
}
